import delay from '@redux-saga/delay-p';
import invariant from 'invariant';
import get from 'lodash/get';
import { END, eventChannel } from 'redux-saga';
import { all, call, put, select, take } from 'typed-redux-saga/macro';

import config, { getRoute } from 'Config';
import { LEVER_BACKEND_DRIVEN_WEB_CAPTCHA_ENABLED } from 'Lib/ConfigService/levers';
import {
  I13N_ACTION_COMPLETE_LOGIN,
  I13N_ACTION_COMPLETE_REGISTRATION,
  I13N_CATEGORY_LOGIN,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';
import {
  LOGIN_TYPE_FACEBOOK,
  LOGIN_TYPE_GOOGLE,
  LOGIN_TYPE_LINE,
  LOGIN_TYPE_SMS,
} from 'Lib/constants/loginTypes';
import { ONE_MINUTE, ONE_SECOND } from 'Lib/constants/time';
import {
  APPEALABLE_BANNED_USER_ERROR,
  HTTPErrorBase,
  LoggedOutFacebookError,
  SELFIE_CHALLENGE_V2_USER_ERROR,
  SERVER_SIDE_API_ERROR,
  UNDERAGE_USER_ERROR,
} from 'Lib/errors';
import FbAPIClient from 'Lib/FbAPI';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { flush } from 'Lib/persist';
import type { Action, GetActionByType } from 'Lib/reduxType';
import { processResponse } from 'Lib/reduxUtils';
import {
  EXECUTOR_REAUTH,
  getWardenRedirect,
  SELFIE_CHALLENGE_V2_REDIRECT,
  serviceExecutor,
  withErrorHandling,
} from 'Lib/sagaHelpers';
import session from 'Lib/Session';
import { getLeverValue } from 'Lib/utils/getLeverValue';
import { getValue } from 'Lib/utils/protobufUtils';
import { getRedirectUri } from 'Lib/utils/redirectUri';
import services, { getServiceFn } from 'Services';
import { AUTH_SERVICE, ONBOARDING_SERVICE } from 'Services/constants';

import { getLineAccessToken, submitLineTokenConnectAccount } from 'Store/account/actions';
import { GET_LINE_ACCESS_TOKEN } from 'Store/account/constants';
// App
import { deviceFlaggedForDobViolation, getExperiments } from 'Store/app/actions';
// Auth attribution
import { getAuthAttributionData } from 'Store/app/selectors';
import { setBanPolicy } from 'Store/appeals/actions';
import {
  ASYNC_FEATURE_AUTH_ONBOARDING,
  ASYNC_FEATURE_USER_PROFILE,
  loadAsyncBundle,
} from 'Store/asyncSaga';
import { guestOnboardingInit, onboardingInit } from 'Store/authOnboarding/actions';
// Onboarding
import { COMPLETE_ONBOARDING_SUCCESS } from 'Store/authOnboarding/constants';
// Captcha
import {
  CAPTCHA_EXPERIMENT_TYPE_NONE,
  CAPTCHA_REASON_APPEALABLE_BAN,
  CAPTCHA_REASON_ONBOARDING,
  CAPTCHA_VERIFY_SUCCESS,
} from 'Store/captcha/constants';
import {
  sendEmailError,
  sendEmailSuccess,
  setEmailAuthState,
  setEmailOtpLength,
  setMarketingEmailOptions,
  setMaskedEmail,
  setSecondFactorThirdParty,
  validateEmailOtpError,
  validateEmailOtpSuccess,
  validateGoogleTokenError,
  validateGoogleTokenSuccess,
} from 'Store/email/actions';
// Email
import {
  EMAIL_AUTH_STATE_EMAIL,
  EMAIL_AUTH_STATE_OTP,
  EMAIL_AUTH_STATE_THIRD_PARTY_NOT_FOUND,
  EMAIL_AUTH_STATE_THIRD_PARTY_VERIFICATION,
} from 'Store/email/constants';
import { validateFacebookTokenError, validateFacebookTokenSuccess } from 'Store/facebook/actions';
// Facebook
import { FACEBOOK_SDK_INITIALIZED } from 'Store/facebook/constants';
import { getIsFacebookInitialized } from 'Store/facebook/selectors';
// Line
import {
  retrieveLineTokenError,
  validateLineTokenError,
  validateLineTokenSuccess,
} from 'Store/line/actions';
// MFA
import {
  authHealthy,
  authOutage,
  handleAuthResponse,
  loginFailure,
  loginSuccess,
  logoutFailure,
  logoutSuccess,
  setAppealToken,
  setAuthToken,
  setAuthTokenExpiration,
  setCaptchaKey,
  setCaptchaType,
  setOnboardingToken,
  setOnboardingUserId,
  setRefreshToken,
  setSelfieChallengeToken,
  setSelfieChallengeV2Status,
  submitFacebookToken,
  submitGoogleToken,
  submitLineToken,
} from 'Store/mfa/actions';
import { healthCheckResponseSchema } from 'Store/mfa/schemas';
import {
  getAuthFunnelSessionId,
  getAuthToken,
  getCaptchaType,
  getIsAuthOutage,
  getIsGuestAuth,
  getLoginType,
  getOnboardingToken,
  getRefreshToken,
  getSmsCaptchaToken,
} from 'Store/mfa/selectors';
import {
  ACTION_SUBMIT,
  CONTEXT_FAIL,
  CONTEXT_SUCCESS,
  DECISION_NO,
  DECISION_YES,
  STEP_AUTH_END,
  STEP_EMAIL_OTP,
} from 'Store/mfa/tracking';
import { closeModal, openModal } from 'Store/modals/actions';
// Modals
import {
  MODAL_AGE_GATE,
  MODAL_EMAIL_AUTH,
  MODAL_EXPIRED_LINK,
  MODAL_LOGIN,
  MODAL_OUTAGE_PREAUTH,
  MODAL_OUTAGE_REAUTH,
  MODAL_SMS_AUTH,
} from 'Store/modals/constants';
import { selectCurrentModal } from 'Store/modals/selectors';
import { navigate } from 'Store/routing/actions';
import { getPathName, getUrlSearch } from 'Store/routing/selectors';
// SMS
import {
  postPhoneNumberError,
  postPhoneNumberSuccess,
  setOtpSending,
  setPhoneNumber,
  setPhoneNumberSending,
  setSMSAuthState,
  setSMSCaptchaReferenceToken,
  submitSMSCaptchaError,
  validateSmsCodeError,
  validateSmsCodeSuccess,
} from 'Store/sms/actions';
import {
  SMS_AUTH_ANALYTICS_ACTION_ERROR,
  SMS_AUTH_ANALYTICS_ACTION_SUBMIT,
  SMS_AUTH_ANALYTICS_ACTION_VIEW,
  SMS_AUTH_ANALYTICS_CHALLENGE_TYPE,
  SMS_AUTH_ANALYTICS_EVENT_TRUST_CHALLENGE,
  SMS_AUTH_STATE_CAPTCHA,
  SMS_AUTH_STATE_CODE,
  SMS_AUTH_STATE_PHONE,
  SMS_CAPTCHA_COMPLETED,
} from 'Store/sms/constants';
import { getPhoneNumber, getSMSCaptchaReferenceToken } from 'Store/sms/selectors';
import {
  clearDeeplinkOtp,
  postLogin,
  setAppealableBanChallengeToken,
  setArkoseDataBlob,
  setIsUnderageOnboarding,
  setUnderageToken,
  storePostLoginRedirectPath,
} from 'Store/user/actions';
// User
import {
  DEEPLINK_TYPE_CAMPAIGN,
  DEEPLINK_TYPE_MAGIC_LINK_WEB_DISCOUNT,
} from 'Store/user/constants';
import { getDeepLinkType, getIsUserLoggedIn, getPostLoginRedirectPath } from 'Store/user/selectors';

// Tracking
import { trackEvent } from 'Store/sparks/actions';
import {
  AUTH_START,
  AUTH_RECOVER_ACCOUNT as EVENT_AUTH_RECOVER_ACCOUNT,
  AUTH_SUCCESS as EVENT_AUTH_SUCCESS,
  IDENTITY_MFA as EVENT_IDENTITY_MFA,
  LOGIN_SUCCESS as EVENT_LOGIN_SUCCESS,
  LOGOUT_ERROR as EVENT_LOGOUT_ERROR,
  LOGOUT_SUCCESS as EVENT_LOGOUT_SUCCESS,
  IDENTITY_INTERACT,
  LEGACY_AUTH_START_LOCATION_REAUTH,
} from 'Store/sparks/sparks.events';

import {
  HANDLE_AUTH_RESPONSE,
  LINE_CONNECT_AUTH,
  LOGIN_START,
  RESEND_PHONE_OTP,
  SUBMIT_ACCOUNT_RECOVERY_OTP,
  SUBMIT_EMAIL,
  SUBMIT_EMAIL_OTP,
  SUBMIT_FACEBOOK_TOKEN,
  SUBMIT_GOOGLE_TOKEN,
  SUBMIT_MAGIC_LINK_OTP,
  SUBMIT_PHONE_NUMBER,
  SUBMIT_PHONE_OTP,
} from './constants';
import { getCaptchaSessionIDFromToken } from './utils';

const { executeEvent } = getI13nUtils();

const ERROR_CODE_BLOCKLISTED_USER = 40303;
const ERROR_CODE_APPEAL_BAN = 40307;
const ERROR_CODE_TO_SKIP = new Set([ERROR_CODE_BLOCKLISTED_USER, ERROR_CODE_APPEAL_BAN]);
const shouldLogError = (e?: any) => {
  const errorCode = e?.error?.code;
  if (!errorCode) {
    return true;
  }

  return !ERROR_CODE_TO_SKIP.has(errorCode);
};

const generateCSRFString = () => Math.random().toString(36).substring(2, 15);

export const loginWithLineSaga = withErrorHandling(
  function* _loginWithLineSaga(action: GetActionByType<typeof GET_LINE_ACCESS_TOKEN>) {
    const { clientId, connectAccountRequest } = action.payload;
    const state = generateCSRFString();
    const { lineOAuthUrl, popupHeight, popupWidth, responseType, lineRedirectUri, scope } =
      config.lineAppSettings;
    const left = screen.width / 2 - popupWidth / 2;
    const top = screen.height / 2 - popupHeight / 2;
    const windowStyle = `width=${popupWidth},height=${popupHeight},top=${top},left=${left},scrollbars=yes`;
    const lineWindowUrl = `${lineOAuthUrl}?response_type=${responseType}&client_id=${clientId}&state=${state}&scope=${scope}&redirect_uri=${getRedirectUri(
      lineRedirectUri
    )}`;
    const win = window.open(lineWindowUrl, 'LineLogin', windowStyle);
    const channel = yield* call(
      createLineOauthChannel,
      state,
      clientId as string,
      connectAccountRequest
    );
    try {
      while (true) {
        const lineAction = (yield* take(channel)) as Action;
        yield* put(lineAction);
      }
    } finally {
      win?.close();
      channel?.close(); // Unsubscribe popup window listeners
    }
  },
  {
    name: 'loginWithLineSaga',
    *errorHandler() {
      yield* put(retrieveLineTokenError());
    },
  }
);

async function createLineOauthChannel(
  initialState: string,
  clientId: string,
  connectAccountRequest?: boolean
) {
  return eventChannel((emit) => {
    const completionTimeout = setTimeout(() => {
      emit(END);
    }, ONE_MINUTE * 2);
    const handleLineLocalStorage = (e: StorageEvent) => {
      if (e.key === LINE_CONNECT_AUTH) {
        const { code: lsCode, state } = JSON.parse(e.newValue || '{}');
        if (lsCode && state === initialState) {
          if (connectAccountRequest) {
            emit(submitLineTokenConnectAccount(lsCode, clientId));
          } else {
            emit(submitLineToken(lsCode, clientId));
          }
        }
        emit(END);
      }
    };
    const handleLinePostMessage = (msg) => {
      if (
        msg.data.code &&
        msg.origin === window.location.origin &&
        msg.data.type === LINE_CONNECT_AUTH &&
        msg.data.state === initialState
      ) {
        if (connectAccountRequest) {
          emit(submitLineTokenConnectAccount(msg.data.code, clientId));
        } else {
          emit(submitLineToken(msg.data.code, clientId));
        }
      }
      emit(END);
    };
    window.addEventListener('storage', handleLineLocalStorage);
    window.addEventListener('message', handleLinePostMessage);
    return () => {
      clearTimeout(completionTimeout);
      window.removeEventListener('storage', handleLineLocalStorage);
      window.removeEventListener('message', handleLinePostMessage);
      localStorage.removeItem(LINE_CONNECT_AUTH);
    };
  });
}

export function* _handleAuthResponseSaga(action: GetActionByType<typeof HANDLE_AUTH_RESPONSE>) {
  const { response, isAccountRecovery, isMagicLink } = action.payload;
  const {
    captchaState,
    getPhoneState,
    validatePhoneOtpState,
    getEmailState,
    loginResult,
    onboardingState,
    validateEmailOtpState,
    validateExistingPhoneState,
    validateGoogleState,
    validateFacebookState,
    facebookAccountNotFound,
    googleAccountNotFound,
    temporaryLoginResult,
  } = response;
  const validateThirdParty = validateGoogleState || validateFacebookState;
  const thirdPartyNotFound = googleAccountNotFound || facebookAccountNotFound;
  const isGuestAuth = yield* select(getIsGuestAuth);

  if (captchaState) {
    const { captchaPublicKey } = captchaState;
    yield* put(setCaptchaKey(captchaPublicKey));
    yield* put(setSMSAuthState(SMS_AUTH_STATE_CAPTCHA));
    yield* put(
      trackEvent(SMS_AUTH_ANALYTICS_EVENT_TRUST_CHALLENGE, {
        actionName: SMS_AUTH_ANALYTICS_ACTION_VIEW,
        challengeSessionId: null,
        challengeType: SMS_AUTH_ANALYTICS_CHALLENGE_TYPE,
      })
    );
    yield* put(setSMSCaptchaReferenceToken(getValue(captchaState, 'referenceToken')));
  }

  if (validateThirdParty) {
    const thirdParty = validateGoogleState ? 'google' : 'facebook';
    const maskedEmail = getValue(validateThirdParty, 'maskedEmail');
    yield* put(setRefreshToken(getValue(validateThirdParty, 'refreshToken')));
    yield* put(setMaskedEmail(maskedEmail));
    yield* put(setSecondFactorThirdParty(thirdParty));
    yield* put(setEmailAuthState(EMAIL_AUTH_STATE_THIRD_PARTY_VERIFICATION));
    yield* put(openModal(MODAL_EMAIL_AUTH));
  }

  if (thirdPartyNotFound) {
    const thirdParty = googleAccountNotFound ? 'google' : 'facebook';
    const maskedEmail = getValue(thirdPartyNotFound, 'maskedEmail');
    yield* put(setRefreshToken(getValue(thirdPartyNotFound, 'refreshToken')));
    yield* put(setMaskedEmail(maskedEmail));
    yield* put(setSecondFactorThirdParty(thirdParty));
    yield* put(setEmailAuthState(EMAIL_AUTH_STATE_THIRD_PARTY_NOT_FOUND));
    yield* put(openModal(MODAL_EMAIL_AUTH));
  }

  if (getPhoneState) {
    const refreshToken = getValue(getPhoneState, 'refreshToken');
    yield* put(setRefreshToken(refreshToken));
    yield* put(setSMSAuthState(SMS_AUTH_STATE_PHONE));
    yield* put(
      openModal(MODAL_SMS_AUTH, {
        props: {
          isAccountRecovery,
        },
      })
    );
  }

  if (validatePhoneOtpState) {
    const otpLength = getValue(validatePhoneOtpState, 'otpLength');
    const smsSent = getValue(validatePhoneOtpState, 'smsSent', false);
    yield* put(postPhoneNumberSuccess(otpLength, smsSent));
    yield* put(setSMSAuthState(SMS_AUTH_STATE_CODE));
  }

  if (validateExistingPhoneState) {
    const otpLength = getValue(validateExistingPhoneState, 'otpLength');
    const smsSent = getValue(validateExistingPhoneState, 'smsSent', false);
    const maskedPhoneNumber = getValue(validateExistingPhoneState, 'maskedPhoneNumber');
    const refreshToken = getValue(validateExistingPhoneState, 'refreshToken');
    yield* put(setRefreshToken(refreshToken));

    yield* put(setPhoneNumber(maskedPhoneNumber));
    yield* put(postPhoneNumberSuccess(otpLength, smsSent));
    yield* put(setSMSAuthState(SMS_AUTH_STATE_CODE));
    yield* put(
      openModal(MODAL_SMS_AUTH, {
        props: {
          isMagicLink,
        },
      })
    );
  }

  if (getEmailState) {
    const emailMarketing = getValue(getEmailState, 'emailMarketing');
    yield* put(
      setMarketingEmailOptions({
        showMarketingOptIn: getValue(emailMarketing, 'showMarketingOptIn'),
        showStrictOptIn: getValue(emailMarketing, 'showStrictOptIn'),
        checkedByDefault: getValue(emailMarketing, 'checkedByDefault'),
      })
    );
    yield* put(setRefreshToken(getValue(getEmailState, 'refreshToken')));
    yield* put(setEmailAuthState(EMAIL_AUTH_STATE_EMAIL));

    if (!isGuestAuth) {
      yield* put(openModal(MODAL_EMAIL_AUTH));
    }
  }

  if (validateEmailOtpState) {
    const refreshToken = getValue(validateEmailOtpState, 'refreshToken');
    const otpLength = getValue(validateEmailOtpState, 'otpLength');
    const maskedEmail = getValue(validateEmailOtpState, 'maskedEmail');
    const emailMarketing = getValue(validateEmailOtpState, 'emailMarketing');
    yield* put(setRefreshToken(refreshToken));
    yield* put(setEmailOtpLength(otpLength));
    yield* put(setMaskedEmail(maskedEmail));
    if (emailMarketing) {
      yield* put(
        setMarketingEmailOptions({
          showMarketingOptIn: getValue(emailMarketing, 'showMarketingOptIn'),
          showStrictOptIn: getValue(emailMarketing, 'showStrictOptIn'),
          checkedByDefault: getValue(emailMarketing, 'checkedByDefault'),
        })
      );
    }
    yield* put(setEmailAuthState(EMAIL_AUTH_STATE_OTP));
    const currentModal = yield* select(selectCurrentModal);
    if (currentModal?.type !== MODAL_EMAIL_AUTH) {
      yield* put(openModal(MODAL_EMAIL_AUTH));
    }
  }

  if (loginResult) {
    const authToken = getValue(loginResult, 'authToken');
    const authTokenTtl = getValue(loginResult, 'authTokenTtl');
    const refreshToken = getValue(loginResult, 'refreshToken');
    const captcha = getValue(loginResult, 'captcha');
    const authTokenExpiration = authTokenTtl ? Date.now() + parseInt(authTokenTtl, 10) : null;
    yield* put(setAuthToken(authToken));
    yield* put(setAuthTokenExpiration(authTokenExpiration));
    yield* put(setRefreshToken(refreshToken));
    yield* put(setCaptchaType(captcha));
    yield* call(handleLoginSaga);
  }

  if (temporaryLoginResult) {
    const authToken = getValue(temporaryLoginResult, 'authToken');
    const authTokenTtl = getValue(temporaryLoginResult, 'authTokenTtl');
    const authTokenExpiration = authTokenTtl ? Date.now() + parseInt(authTokenTtl, 10) : null;
    yield* put(setAuthToken(authToken));
    yield* put(setAuthTokenExpiration(authTokenExpiration));
    yield* call(handleLoginSaga);
  }

  if (onboardingState) {
    const onboardingToken = getValue(onboardingState, 'onboardingToken');
    const onboardingUserId = getValue(onboardingState, 'userId');
    const refreshToken = getValue(onboardingState, 'refreshToken');
    yield* call(
      yield* call(getServiceFn, ONBOARDING_SERVICE, 'setOnboardingToken' as const),
      onboardingToken
    );
    yield* put(setOnboardingToken(onboardingToken));
    yield* put(setOnboardingUserId(onboardingUserId));
    yield* put(setRefreshToken(refreshToken));

    if (isGuestAuth) {
      yield serviceExecutor([services.apiClient, 'setApiToken'], onboardingToken);
      yield* call(handleGuestOnboardingSaga);
    } else {
      yield* call(handleOnboardingSaga);
    }
  }
}

export const handleAuthResponseSaga = withErrorHandling(_handleAuthResponseSaga, {
  name: 'handleAuthResponseSaga',
  *errorHandler(e) {
    yield* call(handleLoginFailure, e);
  },
});

function* handleCloseModal() {
  yield* put(navigate(getRoute('login', {}, { withPrefix: false })));
}

function* handleAuthError(e) {
  const { name, isBan, appealToken, policy } = e || {};

  if (appealToken) {
    yield* put(setAppealToken(appealToken));
    // TODO: wait for LOGIN_SUCCESS / LOGIN_FAILURE to run fetchCases
    yield* put(setAuthToken(''));
  }

  if (policy) {
    yield* put(setBanPolicy(policy));
  }

  // Outage
  if (name === SERVER_SIDE_API_ERROR) {
    yield* call(handleOutage);
  }

  // Banned
  if (isBan) {
    const currentPath = yield* select(getPathName);
    const appealsCenterPath = getRoute('appealsCenter', undefined, { withPrefix: false });
    // TRUST-3377 - for now, do not allow underage banned users to access appeals center
    if (currentPath === appealsCenterPath && name !== UNDERAGE_USER_ERROR) {
      return;
    }

    yield* put(closeModal());

    const wardenRedirect = getWardenRedirect(name, policy);

    if (name === APPEALABLE_BANNED_USER_ERROR) {
      const { challengeToken, refreshToken, arkoseDataBlob } = e;
      yield* all([
        put(setAppealableBanChallengeToken(challengeToken)),
        put(setArkoseDataBlob(arkoseDataBlob)),
        put(setRefreshToken(refreshToken)),
        put(navigate(getRoute('verify', { reason: CAPTCHA_REASON_APPEALABLE_BAN }))),
      ]);
    } else if (name === UNDERAGE_USER_ERROR) {
      const { ttl, isAppealable, isOnboarding, underageToken } = e;
      yield* put(deviceFlaggedForDobViolation(null, ttl, isAppealable));
      yield* put(setIsUnderageOnboarding(isOnboarding));
      yield* put(setUnderageToken(underageToken));
      yield* put(
        openModal(MODAL_AGE_GATE, {
          props: {
            handleCloseModal,
          },
        })
      );
    } else if (name === SELFIE_CHALLENGE_V2_USER_ERROR) {
      const { selfieChallengeToken, selfieStatus } = e;
      yield* all([
        put(setSelfieChallengeToken(selfieChallengeToken)),
        put(setSelfieChallengeV2Status(selfieStatus)),
        put(navigate(SELFIE_CHALLENGE_V2_REDIRECT, { replace: true })),
      ]);
    } else if (wardenRedirect) {
      yield* put(navigate(wardenRedirect));
    }
  }
}

function* handleOutage() {
  const outageCurrentlyHandled = yield* select(getIsAuthOutage);
  if (!outageCurrentlyHandled) {
    const isReauth = yield* select(getIsUserLoggedIn);
    const modal = isReauth ? MODAL_OUTAGE_REAUTH : MODAL_OUTAGE_PREAUTH;
    yield* put(closeModal());
    yield* put(authOutage());
    yield* put(
      openModal(modal, {
        props: {
          forceOpen: true,
        },
      })
    );
  }
}

function* handleLoginFailure(e) {
  const isReauth = yield* select(getIsUserLoggedIn);
  const currentModal = yield* select(selectCurrentModal);
  if (!isReauth) {
    yield* put(loginFailure(e));
  }
  if (!currentModal) {
    yield* put(openModal(MODAL_LOGIN));
  }
}

function* _checkAuthHealthSaga() {
  const response = yield* call(yield* call(getServiceFn, AUTH_SERVICE, 'getHealth' as const));
  const { result } = yield processResponse(response, healthCheckResponseSchema);

  if (result === false) {
    yield* call(handleOutage);
  } else {
    yield* put(authHealthy());
  }
}
export const checkAuthHealthSaga = withErrorHandling(_checkAuthHealthSaga, {
  name: 'checkAuthHealthSaga',
  *errorHandler() {
    // Don't block on network failure
    yield* put(authHealthy());
  },
});

function* _loginStartSaga(action: GetActionByType<typeof LOGIN_START>) {
  const { loginType, token, clientId } = action.payload;

  switch (loginType) {
    case LOGIN_TYPE_FACEBOOK: {
      if (!(yield* select(getIsFacebookInitialized))) {
        // TODO: this won't work on most browsers
        yield* take(FACEBOOK_SDK_INITIALIZED);
      }
      const fbLoginStatus = yield* call([FbAPIClient, 'login']);
      if (!fbLoginStatus.authResponse || fbLoginStatus.status !== 'connected') {
        throw new LoggedOutFacebookError();
      }
      const accessToken = get(fbLoginStatus, ['authResponse', 'accessToken']);
      yield* put(submitFacebookToken(accessToken));
      return;
    }
    case LOGIN_TYPE_GOOGLE: {
      invariant(token, 'Google login requires a token');
      yield* put(submitGoogleToken(token, null, true));
      return;
    }
    case LOGIN_TYPE_LINE: {
      // TODO: REMOVE-TYPE-ASSERTION - handle undefined clientId case.
      yield* put(getLineAccessToken(clientId as string, false));
      return;
    }
    case LOGIN_TYPE_SMS:
    default: {
      yield* put(openModal(MODAL_SMS_AUTH));
    }
  }
}
export const loginStartSaga = withErrorHandling(_loginStartSaga, {
  name: 'loginStartSaga',
  *errorHandler(e) {
    yield* call(handleLoginFailure, e);
  },
});

function* _submitPhoneNumberSaga(action: GetActionByType<typeof SUBMIT_PHONE_NUMBER>) {
  const { phoneNumber, isAccountRecovery } = action.payload;
  const isGuestAuth = yield* select(getIsGuestAuth);
  const refreshToken = yield* select(getRefreshToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const authAttributionData = yield* select(getAuthAttributionData);
  const isBackendDrivenWebCaptchaEnabled = getLeverValue(LEVER_BACKEND_DRIVEN_WEB_CAPTCHA_ENABLED);

  yield* put(setPhoneNumber(phoneNumber));
  yield* put(setPhoneNumberSending());
  let response;
  if (isAccountRecovery) {
    response = yield* call(
      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      yield* call(getServiceFn, AUTH_SERVICE, 'submitAccountRecoveryPhone' as const),
      phoneNumber,
      refreshToken,
      funnelSessionId,
      false,
      isGuestAuth
    );
  } else if (isBackendDrivenWebCaptchaEnabled) {
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'submitPhone' as const),
      phoneNumber,
      refreshToken,
      funnelSessionId,
      false,
      isGuestAuth,
      authAttributionData
    );
  } else {
    const smsCaptchaToken = yield* select(getSmsCaptchaToken);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'legacySubmitPhone' as const),
      phoneNumber,
      refreshToken,
      smsCaptchaToken,
      funnelSessionId,
      false,
      isGuestAuth,
      authAttributionData
    );
  }
  yield* put(handleAuthResponse(response));
}

export const submitPhoneNumberSaga = withErrorHandling(_submitPhoneNumberSaga, {
  name: 'submitPhoneNumberSaga',
  *errorHandler(e) {
    const { error } = e as HTTPErrorBase;
    yield* put(postPhoneNumberError(error));
    yield* call(handleAuthError, e);
  },
});

function* _resendPhoneOtpSaga(action: GetActionByType<typeof RESEND_PHONE_OTP>) {
  const { isAccountRecovery, isMagicLink } = action.payload;
  const isGuestAuth = yield* select(getIsGuestAuth);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const refreshToken = yield* select(getRefreshToken);
  const phoneNumber = yield* select(getPhoneNumber);
  const isBackendDrivenWebCaptchaEnabled = getLeverValue(LEVER_BACKEND_DRIVEN_WEB_CAPTCHA_ENABLED);
  const authAttributionData = yield* select(getAuthAttributionData);

  let response;
  if (isAccountRecovery) {
    response = yield* call(
      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      yield* call(getServiceFn, AUTH_SERVICE, 'submitAccountRecoveryPhone' as const),
      phoneNumber,
      refreshToken,
      funnelSessionId,
      false,
      isGuestAuth
    );
  } else if (isMagicLink) {
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'resendMagicLinkPhoneOtp' as const),
      refreshToken,
      funnelSessionId
    );
  } else if (isBackendDrivenWebCaptchaEnabled) {
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'resendPhoneOtp' as const),
      phoneNumber,
      refreshToken,
      funnelSessionId,
      isGuestAuth,
      authAttributionData
    );
  } else {
    const smsCaptchaToken = yield* select(getSmsCaptchaToken);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'legacySubmitPhone' as const),
      phoneNumber,
      refreshToken,
      smsCaptchaToken,
      funnelSessionId,
      false,
      isGuestAuth,
      authAttributionData
    );
  }
  yield* put(handleAuthResponse(response));
}

export const resendPhoneOtpSaga = withErrorHandling(_resendPhoneOtpSaga, {
  name: 'resendPhoneOtpSaga',
  *errorHandler(e) {
    yield* call(handleAuthError, e);
  },
});

export function* _submitSMSCaptchaSaga(action: GetActionByType<typeof SMS_CAPTCHA_COMPLETED>) {
  // Logic needed for the rollout experiment. Once rollout is complete, this logic can be removed.
  const isBackendDrivenWebCaptchaEnabled = getLeverValue(LEVER_BACKEND_DRIVEN_WEB_CAPTCHA_ENABLED);
  if (!isBackendDrivenWebCaptchaEnabled) {
    yield* put(setSMSAuthState(SMS_AUTH_STATE_PHONE));
    return;
  }

  const { smsCaptchaToken } = action.payload;
  const isGuestAuth = yield* select(getIsGuestAuth);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const smsCaptchaReferenceToken = yield* select(getSMSCaptchaReferenceToken);
  const challengeSessionId = getCaptchaSessionIDFromToken(smsCaptchaToken);
  const authAttributionData = yield* select(getAuthAttributionData);

  yield* put(
    trackEvent(SMS_AUTH_ANALYTICS_EVENT_TRUST_CHALLENGE, {
      actionName: SMS_AUTH_ANALYTICS_ACTION_SUBMIT,
      challengeSessionId,
      challengeType: SMS_AUTH_ANALYTICS_CHALLENGE_TYPE,
    })
  );

  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'submitSMSCaptcha' as const),
    smsCaptchaToken,
    smsCaptchaReferenceToken,
    funnelSessionId,
    isGuestAuth,
    authAttributionData
  );
  yield* put(handleAuthResponse(response));
}

export const submitSMSCaptchaSaga = withErrorHandling(_submitSMSCaptchaSaga, {
  name: 'submitSMSCaptchaSaga',
  *errorHandler(e) {
    const { error } = e as HTTPErrorBase;
    const smsCaptchaToken = yield* select(getSmsCaptchaToken);
    const challengeSessionId = getCaptchaSessionIDFromToken(smsCaptchaToken);
    yield* put(setSMSAuthState(SMS_AUTH_STATE_PHONE));
    yield* put(postPhoneNumberError(error));
    yield* put(submitSMSCaptchaError(error));
    yield* put(
      trackEvent(SMS_AUTH_ANALYTICS_EVENT_TRUST_CHALLENGE, {
        actionName: SMS_AUTH_ANALYTICS_ACTION_ERROR,
        challengeSessionId,
        challengeType: SMS_AUTH_ANALYTICS_CHALLENGE_TYPE,
        errorMessage: e.message,
      })
    );
    yield* call(handleAuthError, e);
  },
});

function* _submitPhoneOtpSaga(action: GetActionByType<typeof SUBMIT_PHONE_OTP>) {
  const { phoneNumber, otp, isAccountRecovery, isMagicLink } = action.payload;
  const isGuestAuth = yield* select(getIsGuestAuth);
  const refreshToken = yield* select(getRefreshToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  yield* put(setOtpSending());
  let response;
  if (isAccountRecovery) {
    response = yield* call(
      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      yield* call(getServiceFn, AUTH_SERVICE, 'submitAccountRecoveryPhoneOtp' as const),
      phoneNumber,
      otp,
      refreshToken,
      funnelSessionId,
      false,
      isGuestAuth
    );
  } else if (isMagicLink) {
    response = yield* call(
      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      yield* call(getServiceFn, AUTH_SERVICE, 'submitMagicLinkPhoneOtp' as const),
      otp,
      refreshToken,
      funnelSessionId,
      false,
      isGuestAuth
    );
  } else {
    const authAttributionData = yield* select(getAuthAttributionData);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'submitPhoneOtp' as const),
      phoneNumber,
      otp,
      refreshToken,
      funnelSessionId,
      false,
      isGuestAuth,
      authAttributionData
    );
  }
  // Response can be 1. login/onboarding or 2. collect email
  yield* put(handleAuthResponse(response));
  yield* put(validateSmsCodeSuccess());
}
export const submitPhoneOtpSaga = withErrorHandling(_submitPhoneOtpSaga, {
  name: 'submitPhoneOtpSaga',
  *errorHandler(e) {
    const { error } = e as HTTPErrorBase;
    yield* put(validateSmsCodeError(error));
    yield* call(handleAuthError, e);
  },
  shouldLogError,
});

function* _resendEmailOtpSaga() {
  const refreshToken = yield* select(getRefreshToken);
  const isGuestAuth = yield* select(getIsGuestAuth);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const authAttributionData = yield* select(getAuthAttributionData);
  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'resendEmailOtp' as const),
    refreshToken,
    funnelSessionId,
    false,
    isGuestAuth,
    authAttributionData
  );
  yield* put(handleAuthResponse(response));
}

export const resendEmailOtpSaga = withErrorHandling(_resendEmailOtpSaga, {
  name: 'resendEmailOtpSaga',
  *errorHandler(e) {
    yield* call(handleAuthError, e);
  },
});

function* _submitEmailSaga(action: GetActionByType<typeof SUBMIT_EMAIL>) {
  const { email, marketingOptIn } = action.payload;
  const isGuestAuth = yield* select(getIsGuestAuth);
  const refreshToken = yield* select(getRefreshToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const authAttributionData = yield* select(getAuthAttributionData);
  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'submitEmail' as const),
    email,
    marketingOptIn,
    refreshToken,
    funnelSessionId,
    false,
    isGuestAuth,
    authAttributionData
  );
  // Response can be 1. login, 2. collect otp or 3. onboarding
  yield* put(handleAuthResponse(response));
  yield* put(sendEmailSuccess());
}
export const submitEmailSaga = withErrorHandling(_submitEmailSaga, {
  name: 'submitEmailSaga',
  *errorHandler(e) {
    const { error } = e as HTTPErrorBase;
    yield* put(sendEmailError(error));
    yield* call(handleAuthError, e);
  },
});

function* _submitEmailOtpSaga(action: GetActionByType<typeof SUBMIT_EMAIL_OTP>) {
  const { email, otp } = action.payload;
  const isGuestAuth = yield* select(getIsGuestAuth);
  const refreshToken = yield* select(getRefreshToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const authAttributionData = yield* select(getAuthAttributionData);
  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'submitEmailOtp' as const),
    otp,
    email,
    refreshToken,
    funnelSessionId,
    false,
    isGuestAuth,
    authAttributionData
  );
  // Response can be 1. login or 2. onboarding
  yield* put(handleAuthResponse(response));
  yield* put(validateEmailOtpSuccess());

  yield* put(
    trackEvent(EVENT_IDENTITY_MFA, {
      stepName: STEP_EMAIL_OTP,
      actionName: ACTION_SUBMIT,
      actionContext: CONTEXT_SUCCESS,
      funnelSessionId,
    })
  );
}
export const submitEmailOtpSaga = withErrorHandling(_submitEmailOtpSaga, {
  name: 'submitEmailOtpSaga',
  *errorHandler(e) {
    const { error } = e as HTTPErrorBase;
    const funnelSessionId = yield* select(getAuthFunnelSessionId);
    yield* put(validateEmailOtpError(error));
    yield* put(
      trackEvent(EVENT_IDENTITY_MFA, {
        stepName: STEP_EMAIL_OTP,
        actionName: ACTION_SUBMIT,
        actionContext: CONTEXT_FAIL,
        funnelSessionId,
      })
    );
    yield* call(handleAuthError, e);
  },
});

export const submitLineTokenSaga = withErrorHandling(
  function* (action) {
    const { lineToken, clientId } = action.payload;
    if (!lineToken) return;
    const funnelSessionId = yield* select(getAuthFunnelSessionId);
    const authAttributionData = yield* select(getAuthAttributionData);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    const response = yield* call(
      yield* call(getServiceFn, AUTH_SERVICE, 'submitLineToken' as const),
      lineToken,
      clientId,
      funnelSessionId,
      false,
      authAttributionData
    );
    yield* put(handleAuthResponse(response));
    yield* put(validateLineTokenSuccess());
  },
  {
    name: 'submitLineTokenSaga',
    *errorHandler(e) {
      yield* put(validateLineTokenError());
      yield* call(handleAuthError, e);
    },
  }
);

function* _submitGoogleTokenSaga(action: GetActionByType<typeof SUBMIT_GOOGLE_TOKEN>) {
  const { googleIdToken, marketingOptIn, isLoginStart } = action.payload;
  const refreshToken = yield* select(getRefreshToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const authAttributionData = yield* select(getAuthAttributionData);
  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'submitGoogleToken' as const),
    googleIdToken,
    marketingOptIn,
    isLoginStart,
    refreshToken,
    funnelSessionId,
    false,
    authAttributionData
  );
  yield* put(handleAuthResponse(response));
  yield* put(validateGoogleTokenSuccess());
}

export const submitGoogleTokenSaga = withErrorHandling(_submitGoogleTokenSaga, {
  name: 'submitGoogleTokenSaga',
  *errorHandler(e) {
    const { error } = e as HTTPErrorBase;
    yield* put(validateGoogleTokenError(error));
    yield* call(handleAuthError, e);
  },
  shouldLogError,
});

function* _submitFacebookTokenSaga(action: GetActionByType<typeof SUBMIT_FACEBOOK_TOKEN>) {
  const { facebookToken } = action.payload;
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const authAttributionData = yield* select(getAuthAttributionData);
  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'submitFacebookToken' as const),
    facebookToken,
    funnelSessionId,
    false,
    authAttributionData
  );
  yield* put(handleAuthResponse(response));
  yield* put(validateFacebookTokenSuccess());
}

export const submitFacebookTokenSaga = withErrorHandling(_submitFacebookTokenSaga, {
  name: 'submitFacebookTokenSaga',
  *errorHandler(e) {
    yield* put(validateFacebookTokenError());
    yield* call(handleAuthError, e);
  },
  shouldLogError,
});

function* _submitAccountRecoveryOtpSaga(
  action: GetActionByType<typeof SUBMIT_ACCOUNT_RECOVERY_OTP>
) {
  const otp = action.payload.recoveryOtp;
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'verifyEmailMagicLinkOtp' as const),
    otp
  );
  yield* put(handleAuthResponse(response, true));
  yield* put(
    trackEvent(EVENT_AUTH_RECOVER_ACCOUNT, {
      step: 'emailLink',
      action: 'verify',
      value: null,
      status: 1,
      source: null,
    })
  );
  yield* put(clearDeeplinkOtp());
}

export const submitAccountRecoveryOtpSaga = withErrorHandling(_submitAccountRecoveryOtpSaga, {
  name: 'submitAccountRecoveryOtpSaga',
  *errorHandler(e) {
    const otpType = yield* select(getDeepLinkType);
    yield* put(
      trackEvent(EVENT_AUTH_RECOVER_ACCOUNT, {
        step: 'emailLink',
        action: 'verify',
        value: null,
        status: 0,
        source: null,
      })
    );

    // Don't show expiration error on campaign
    if (otpType !== DEEPLINK_TYPE_CAMPAIGN) {
      yield* put(openModal(MODAL_EXPIRED_LINK));
    }
    yield* call(handleAuthError, e);
  },
  shouldLogError,
});

function* _handleGuestLoginStartSaga() {
  yield* call(loadAsyncBundle, ASYNC_FEATURE_USER_PROFILE);
  const search = yield* select(getUrlSearch);
  const path = yield* select(getPathName);
  yield* put(
    storePostLoginRedirectPath({
      path: `${path}${search}`,
      pushState: true,
    })
  );
}

export const handleGuestLoginStartSaga = withErrorHandling(_handleGuestLoginStartSaga, {
  name: 'handleGuestLoginStartSaga',
  *errorHandler(e) {
    yield* call(handleAuthError, e);
  },
});

function* _reauthSaga() {
  const refreshToken = yield* select(getRefreshToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  const loginType = yield* select(getLoginType);
  const onboardingToken = yield* select(getOnboardingToken);

  // If not logging in after onboarding
  if (!onboardingToken) {
    yield* put(
      trackEvent(AUTH_START, {
        authFrom: LEGACY_AUTH_START_LOCATION_REAUTH,
        hasPreviouslyLoggedIn: true,
        method: loginType,
      })
    );
  }

  // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'refreshAuth' as const, EXECUTOR_REAUTH),
    refreshToken,
    funnelSessionId
  );
  yield* put(handleAuthResponse(response));
}

export const reauthSaga = withErrorHandling(_reauthSaga, {
  name: 'reauthSaga',
  *errorHandler(e) {
    yield* call(handleAuthError, e);
    let funnelSessionId;
    try {
      funnelSessionId = yield* select(getAuthFunnelSessionId);
    } catch {} // eslint-disable-line
    yield* put(
      trackEvent(IDENTITY_INTERACT, {
        stepName: STEP_AUTH_END,
        decision: DECISION_NO,
        funnelSessionId,
      })
    );
  },
  shouldLogError,
});

function* _authLogoutSaga() {
  const refreshToken = yield* select(getRefreshToken);
  const loginType = yield* select(getLoginType);

  yield* call(yield* call(getServiceFn, AUTH_SERVICE, 'logout' as const), refreshToken);
  if (loginType === LOGIN_TYPE_FACEBOOK) {
    yield* call([FbAPIClient, 'logout']);
  }
  yield serviceExecutor([services.apiClient, 'logout']);

  // Reload pre-auth levers
  yield* put(getExperiments());

  yield* put(navigate(getRoute('login', {}, { withPrefix: false })));
  yield delay(ONE_SECOND); // Ensure previous page is unloaded
  yield* put(trackEvent(EVENT_LOGOUT_SUCCESS, {}, { forceSend: true }));
  yield* put(logoutSuccess());
}

export const authLogoutSaga = withErrorHandling(
  _authLogoutSaga,
  {
    name: 'authLogoutSaga',
    *errorHandler() {
      yield* put(trackEvent(EVENT_LOGOUT_ERROR));
      yield* put(logoutFailure());
      yield* put(navigate(getRoute('login', {}, { withPrefix: false })));
    },
    *finalHandler() {
      yield* put(flush());
    },
  },
  false // Don't re-auth - to prevent infinite loop
);

function* _handleOnboardingSaga() {
  const loginType = yield* select(getLoginType);

  yield* put(trackEvent(EVENT_LOGIN_SUCCESS, { loginType, isNewUser: true }));

  yield* put(closeModal());
  yield* call(loadAsyncBundle, ASYNC_FEATURE_AUTH_ONBOARDING);
  yield* put(onboardingInit());
  yield* take(COMPLETE_ONBOARDING_SUCCESS);
  yield* call(reauthSaga);
}
const handleOnboardingSaga = withErrorHandling(_handleOnboardingSaga, {
  name: 'handleOnboardingSaga',
  *errorHandler(e) {
    yield* call(handleAuthError, e);
  },
});

const handleGuestOnboardingSaga = withErrorHandling(
  // eslint-disable-next-line func-names
  function* () {
    yield* put(closeModal());
    yield* put(guestOnboardingInit());
  },
  {
    name: 'handleGuestOnboardingSaga',
    *errorHandler(e) {
      yield* call(handleAuthError, e);
    },
  }
);

function* _handleLoginSaga() {
  const authToken = yield* select(getAuthToken);
  const captchaType = yield* select(getCaptchaType);
  const loginType = yield* select(getLoginType);
  const onboardingToken = yield* select(getOnboardingToken);
  const isReauth = yield* select(getIsUserLoggedIn);

  if (!isReauth) {
    // Close login or onboarding modal
    yield* put(closeModal());
  }
  yield serviceExecutor([services.apiClient, 'setApiToken'], authToken);
  const funnelSessionId = yield* select(getAuthFunnelSessionId);
  yield* put(
    trackEvent(IDENTITY_INTERACT, {
      stepName: STEP_AUTH_END,
      decision: DECISION_YES,
      funnelSessionId,
    })
  );

  // If not a new user
  if (!onboardingToken) {
    yield* put(trackEvent(EVENT_LOGIN_SUCCESS, { loginType, isNewUser: false }));
  }

  // Is captcha required
  if (captchaType && captchaType !== CAPTCHA_EXPERIMENT_TYPE_NONE) {
    yield* put(navigate(getRoute('verify', { reason: CAPTCHA_REASON_ONBOARDING })));
    yield* take(CAPTCHA_VERIFY_SUCCESS);
  }

  yield* put(loginSuccess());
  session.clear();
  yield* put(trackEvent(EVENT_AUTH_SUCCESS, { method: loginType }));
  executeEvent(I13N_TYPE_FEATURE_COUNT, {
    category: I13N_CATEGORY_LOGIN,
    action: onboardingToken ? I13N_ACTION_COMPLETE_REGISTRATION : I13N_ACTION_COMPLETE_LOGIN,
    label: loginType,
  });
  // Clear deeplink otp if it hasn't already been cleared
  yield* put(clearDeeplinkOtp());
  const postLoginRedirectPath = yield* select(getPostLoginRedirectPath);
  if (!isReauth && !postLoginRedirectPath) {
    yield* put(navigate(getRoute('recs')));
  }
  yield* put(postLogin());
}

const handleLoginSaga = withErrorHandling(_handleLoginSaga, {
  name: 'handleLoginSaga',
  *errorHandler(e) {
    yield* call(handleLoginFailure, e);
    yield* call(handleAuthError, e);
  },
});

function* _submitMagicLinkOtpSaga(action: GetActionByType<typeof SUBMIT_MAGIC_LINK_OTP>) {
  const { otp, type } = action.payload;
  const response = yield* call(
    yield* call(getServiceFn, AUTH_SERVICE, 'verifyLoginMagicLinkOtp' as const),
    otp,
    type
  );

  yield* put(handleAuthResponse(response, false, true));
  // Don't clear deeplink info here if part of web discount flow
  if (type !== DEEPLINK_TYPE_MAGIC_LINK_WEB_DISCOUNT && response.status === 200) {
    yield* put(clearDeeplinkOtp());
  }
}

export const submitMagicLinkOtpSaga = withErrorHandling(_submitMagicLinkOtpSaga, {
  name: 'submitMagicLinkOtpSaga',
  *errorHandler(e) {
    yield* call(handleAuthError, e);
    const postLoginRedirectPath = yield* select(getPostLoginRedirectPath);
    yield* put(navigate(postLoginRedirectPath || getRoute('login')));
  },
  shouldLogError,
});
